import { LivekitBaseController } from "./livekit_base_controller"
import { RoomEvent, Track } from "livekit-client"

// ========================================
// LiveKit Voice Mode Controller
// ========================================
// This controller manages voice-to-voice conversation with AI
// using LiveKit's WebRTC infrastructure and Python agent
//
// Flow: User speaks → STT → Rails LLM → TTS → AI audio plays
//       User can interrupt AI mid-speech
// ========================================

export default class extends LivekitBaseController {
  static targets = ["button", "stopButton", "statusText", "transcript", "interrupt"]
  static values = {
    chatId: Number,
    userSgid: String,
    coachGender: String,
    language: String,
    livekitWsUrl: String
  }

  // ========================================
  // LIFECYCLE METHODS
  // ========================================
  connect() {
    this.initializeState()
  }

  disconnect() {
    this.cleanupResources()
  }

  initializeState() {
    // Voice mode state
    this.isActive = false
    this.room = null

    // Text buffers for transcript display
    this.userTranscript = ""
    this.aiTranscript = ""
    this.partialText = ""

    // Audio element for AI voice
    this.aiAudioElement = null
  }

  cleanupResources() {
    this.stopVoiceMode()
  }

  // ========================================
  // USER INTERACTION
  // ========================================

  // Main entry point - user clicks the voice button
  async toggleVoiceMode() {
    if (this.isActive) {
      this.handleStopVoiceMode()
    } else {
      await this.handleStartVoiceMode()
    }
  }

  // User manually interrupts AI
  async interruptAI() {
    if (!this.room || !this.isActive) return

    console.log('[Voice] User requested interrupt')

    // Send interrupt signal to agent
    const encoder = new TextEncoder()
    const data = encoder.encode(JSON.stringify({ type: "barge_in" }))
    await this.room.localParticipant.publishData(data, { reliable: true, topic: "control" })
  }

  // ========================================
  // VOICE MODE MANAGEMENT
  // ========================================

  async handleStartVoiceMode() {
    try {
      console.log('[Voice] Starting voice mode...')

      // Update UI
      this.updateUIState("connecting")

      // Get LiveKit token with voice mode metadata
      const tokenData = await this.fetchLiveKitToken({
        mode: "voice",
        chat_id: this.chatIdValue,
        user_sgid: this.userSgidValue,
        coach_gender: this.coachGenderValue,
        language: this.languageValue || "en",
      })

      // Connect to LiveKit room with voice-specific event handlers
      await this.connectToRoom(tokenData, {
        onTrackSubscribed: this.handleTrackSubscribed.bind(this),
        onDataReceived: this.handleDataReceived.bind(this)
      })

      // Set active state BEFORE publishing (needed for barge-in detection loop)
      this.isActive = true

      // Publish microphone with aggressive echo cancellation for voice mode
      await this.publishMicrophone({
        echoCancellation: { exact: true },
        noiseSuppression: { exact: true },
        autoGainControl: true,
        sampleRate: 48000,
        channelCount: 1,
      })

      // Update UI state
      this.updateUIState("listening")

    } catch (error) {
      this.handleVoiceError(error)
    }
  }

  handleStopVoiceMode() {
    if (!this.isActive) return

    console.log('[Voice] Stopping voice mode...')

    // Send stop signal to agent
    if (this.room) {
      try {
        const encoder = new TextEncoder()
        const data = encoder.encode(JSON.stringify({ type: "client_stopping" }))
        this.room.localParticipant.publishData(data, { reliable: true, topic: "control" })
      } catch (error) {
        console.error('[Voice] Failed to send stop signal:', error)
      }
    }

    // Stop publishing microphone
    this.stopPublishing()

    // Disconnect from room
    this.disconnectFromRoom()

    // Update state
    this.setActiveState(false)
  }

  // ========================================
  // LIVEKIT EVENT HANDLERS
  // ========================================

  handleTrackSubscribed(track, publication, participant) {
    console.log('[Voice] Track subscribed:', {
      kind: track.kind,
      participantIdentity: participant.identity,
      isAgent: participant.identity?.includes('agent')
    })

    // Subscribe to AI's audio track
    if (track.kind === Track.Kind.Audio && participant.identity?.includes('agent')) {
      console.log('[Voice] AI audio track received, attaching...')

      // Attach to audio element and play
      const audioElement = track.attach()
      audioElement.autoplay = true
      audioElement.volume = 1.0

      // Append to document (required for playback)
      document.body.appendChild(audioElement)
      this.aiAudioElement = audioElement

      console.log('[Voice] AI audio track playing')
    }
  }

  handleDataReceived(payload, participant, kind, topic) {
    if (topic !== "voice") {
      console.log('[Voice] Ignoring non-voice topic:', topic)
      return
    }

    try {
      const text = new TextDecoder().decode(payload)
      const msg = JSON.parse(text)
      console.log('[Voice] Received message:', msg)

      // Track AI speaking state for client-side barge-in
      if (msg.type === "state") {
        this.isAISpeaking = (msg.state === "speaking")
        console.log('[Voice] AI speaking state:', this.isAISpeaking)
      }

      this.handleVoiceMessage(msg)
    } catch (e) {
      console.error("[Voice] Bad payload:", e)
    }
  }

  async publishMicrophone() {
    try {
      // Enable microphone and publish to room with AGGRESSIVE echo cancellation
      await this.room.localParticipant.setMicrophoneEnabled(true, {
        echoCancellation: { exact: true },  // Force echo cancellation
        noiseSuppression: { exact: true },  // Force noise suppression
        autoGainControl: true,
        sampleRate: 48000,
        channelCount: 1,
      })

      console.log('[Voice] Microphone published with aggressive AEC')

      // Small delay to ensure track is available
      await new Promise(resolve => setTimeout(resolve, 200))

      // Get all track publications from local participant
      const trackPublications = this.room.localParticipant.trackPublications

      console.log('[Voice] Track publications:', trackPublications ? trackPublications.size : 'undefined')

      if (trackPublications && trackPublications.size > 0) {
        for (const [, publication] of trackPublications) {
          if (publication.kind === Track.Kind.Audio && publication.track) {
            console.log('[Voice] Found audio track, setting up barge-in detection')
            this.setupClientSideBargeInDetection(publication.track)
            break
          }
        }
      } else {
        console.warn('[Voice] No track publications found')
      }
    } catch (error) {
      console.error('[Voice] Failed to publish microphone:', error)
      throw error
    }
  }

  // Override base disconnectFromRoom to also clean up AI audio element
  disconnectFromRoom() {
    // Remove AI audio element
    if (this.aiAudioElement) {
      this.aiAudioElement.remove()
      this.aiAudioElement = null
    }

    // Call parent method to disconnect room
    super.disconnectFromRoom()
  }

  // ========================================
  // VOICE MESSAGE HANDLING
  // ========================================

  handleVoiceMessage(msg) {
    const { type, text, state } = msg

    switch (type) {
      case "state":
        // UI state update from agent
        this.updateUIState(state)
        break

      case "partial":
        // Partial transcript (user speaking)
        this.partialText = text || ""
        this.renderTranscript()
        break

      case "final":
        // Final transcript (user finished speaking)
        this.userTranscript += (this.userTranscript ? " " : "") + (text || "")
        this.partialText = ""
        this.renderTranscript()
        break

      case "done":
        // Agent finished processing
        console.log('[Voice] Agent processing complete')
        break

      default:
        console.log('[Voice] Unknown message type:', type)
    }
  }

  // ========================================
  // TRANSCRIPT RENDERING
  // ========================================

  renderTranscript() {
    if (!this.hasTranscriptTarget) return

    // Show user's speech (with partial)
    const userText = this.userTranscript + (this.partialText ? ` ${this.partialText}` : "")

    // Build transcript HTML
    let html = ""

    if (userText) {
      html += `<div class="mb-3">
        <div class="font-semibold text-indigo-600 text-sm mb-1">You:</div>
        <div class="text-gray-900">${this.escapeHtml(userText)}</div>
      </div>`
    }

    if (this.aiTranscript) {
      html += `<div class="mb-3">
        <div class="font-semibold text-purple-600 text-sm mb-1">AI:</div>
        <div class="text-gray-900">${this.escapeHtml(this.aiTranscript)}</div>
      </div>`
    }

    this.transcriptTarget.innerHTML = html || '<div class="text-gray-400 text-sm">Transcript will appear here...</div>'
  }

  // ========================================
  // UI UPDATES
  // ========================================

  setActiveState(isActive) {
    this.isActive = isActive
    this.updateUIState(isActive ? "listening" : "idle")
  }

  updateUIState(state) {
    console.log('[Voice] updateUIState called with:', state)
    console.log('[Voice] hasStatusTextTarget:', this.hasStatusTextTarget)
    console.log('[Voice] hasInterruptTarget:', this.hasInterruptTarget)

    if (!this.hasStatusTextTarget) return

    const uiStates = {
      'idle': () => {
        this.showVoiceButton()
        this.hideStopButton()
        this.hideInterruptButton()
        this.hideStatusText()
      },
      'connecting': () => {
        this.hideVoiceButton()
        this.showStopButton()
        this.hideInterruptButton()
        this.showStatusText("🔌 Connecting to AI...")
      },
      'listening': () => {
        this.hideVoiceButton()
        this.showStopButton()
        this.hideInterruptButton()
        this.showStatusText("🎤 Listening... Speak now")
      },
      'thinking': () => {
        this.hideVoiceButton()
        this.showStopButton()
        this.hideInterruptButton()
        this.showStatusText("🤔 AI is thinking...")
      },
      'speaking': () => {
        console.log('[Voice] Entering SPEAKING state - showing interrupt button')
        this.hideVoiceButton()
        this.showStopButton()
        this.showInterruptButton()
        this.showStatusText("🗣️ AI is speaking (click interrupt to stop)")
      },
      'interrupted': () => {
        this.hideInterruptButton()
        this.showStatusText("✋ Interrupted - Listening...")
      },
      'disconnected': () => {
        this.showVoiceButton()
        this.hideStopButton()
        this.hideInterruptButton()
        this.hideStatusText()
      }
    }

    const stateHandler = uiStates[state]
    if (stateHandler) {
      stateHandler()
    }
  }

  showVoiceButton() {
    if (this.hasButtonTarget) {
      this.buttonTarget.classList.remove('hidden')
    }
  }

  hideVoiceButton() {
    if (this.hasButtonTarget) {
      this.buttonTarget.classList.add('hidden')
    }
  }

  showStopButton() {
    if (this.hasStopButtonTarget) {
      this.stopButtonTarget.classList.remove('hidden')
    }
  }

  hideStopButton() {
    if (this.hasStopButtonTarget) {
      this.stopButtonTarget.classList.add('hidden')
    }
  }

  showInterruptButton() {
    if (this.hasInterruptTarget) {
      this.interruptTarget.classList.remove('hidden')
      this.interruptTarget.style.display = 'flex'
      console.log('[Voice] Interrupt button shown')
    }
  }

  hideInterruptButton() {
    if (this.hasInterruptTarget) {
      this.interruptTarget.classList.add('hidden')
      this.interruptTarget.style.display = 'none'
    }
  }

  showStatusText(message) {
    if (this.hasStatusTextTarget) {
      this.statusTextTarget.textContent = message
      this.statusTextTarget.classList.remove('hidden')
    }
  }

  hideStatusText() {
    if (this.hasStatusTextTarget) {
      this.statusTextTarget.classList.add('hidden')
    }
  }

  // ========================================
  // ERROR HANDLING
  // ========================================

  handleVoiceError(error) {
    console.error("Voice mode error:", error)
    this.showError(`Failed to start voice mode: ${error.message}`)
    this.setActiveState(false)
  }

  showError(message) {
    console.error("Voice mode error:", message)

    if (this.hasStatusTextTarget) {
      this.showStatusText(`❌ ${message}`)
    }

    // Show as notification if Turbo is available
    if (window.Turbo) {
      const event = new CustomEvent("turbo:submit-end", {
        detail: {
          success: false,
          error: message
        }
      })
      document.dispatchEvent(event)
    }
  }

  // ========================================
  // CLIENT-SIDE BARGE-IN DETECTION
  // ========================================

  setupClientSideBargeInDetection(track) {
    // Monitor local microphone audio levels to detect user speech
    // This allows interruption even when server can't detect due to echo

    if (!track || !track.mediaStreamTrack) {
      console.error('[Voice] Invalid track provided for barge-in detection')
      return
    }

    console.log('[Voice] Setting up client-side barge-in detection on local track')

    try {
      // Create audio context to analyze microphone input
      const audioContext = new AudioContext()
      const mediaStream = new MediaStream([track.mediaStreamTrack])
      const source = audioContext.createMediaStreamSource(mediaStream)
      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 2048
      source.connect(analyser)

      const bufferLength = analyser.frequencyBinCount
      const dataArray = new Uint8Array(bufferLength)

      const SPEECH_THRESHOLD = 20  // Lowered from 30 - more sensitive
      this.lastBargeInTime = 0

      const checkAudioLevel = () => {
        if (!this.isActive) return

        analyser.getByteFrequencyData(dataArray)
        const average = dataArray.reduce((a, b) => a + b) / bufferLength

        // If AI is speaking and we detect user speech, send barge-in
        if (this.isAISpeaking && average > SPEECH_THRESHOLD) {
          const now = Date.now()
          // Debounce - only send once per 500ms
          if (now - this.lastBargeInTime > 500) {
            this.lastBargeInTime = now
            this.interruptAI()
          }
        }

        requestAnimationFrame(checkAudioLevel)
      }

      checkAudioLevel()
      console.log('[Voice] Client-side barge-in monitoring started')
    } catch (error) {
      console.error('[Voice] Failed to setup barge-in detection:', error)
    }
  }

  // ========================================
  // PUBLIC API ALIASES
  // ========================================

  // Alias for public API
  startVoiceMode() {
    return this.handleStartVoiceMode()
  }

  stopVoiceMode() {
    return this.handleStopVoiceMode()
  }
}
